// @flow
import * as React from 'react';
import { Card, styled, SxProps, Typography } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { Info, Plus } from '@phosphor-icons/react';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

export type MarketplaceInfoAccordionItem = {
  icon?: React.ReactNode;
  summary: React.ReactNode;
  content: React.ReactNode;
  'data-test'?: string;
  sx?: SxProps;
};

type Props = {
  items: MarketplaceInfoAccordionItem[];
};

export function MarketplaceInfoAccordion({ items }: Props) {
  const [expanded, setExpanded] = React.useState<string | false>('panel0');

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Card
      sx={{
        p: 1,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {items.map((item, i) => (
        <Accordion
          key={'panel' + i}
          sx={item.sx}
          expanded={expanded === 'panel' + i}
          onChange={handleChange('panel' + i)}
        >
          <AccordionSummary
            expandIcon={
              <Plus style={{ minWidth: 20 }} size={20} color={'#1C434F'} />
            }
          >
            {item.icon || (
              <Info style={{ minWidth: 20 }} size={20} color={'#D0760F'} />
            )}
            <Typography variant={'body2'} color={'accent.main'}>
              <b>{item.summary}</b>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {typeof item.content === 'string' ? (
              <Typography variant={'body2'}>{item.content}</Typography>
            ) : (
              item.content
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Card>
  );
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderRadius: '8px',
  '&.Mui-expanded': {
    background: '#F3F4F6',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))({
  alignItems: 'flex-start',
  padding: 8,
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(45deg)',
  },
  '& .MuiAccordionSummary-content': {
    alignItems: 'flex-start',
    marginTop: 0,
    marginBottom: 0,
    gap: 6,
  },
});

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingTop: 0,
}));
